import axios from "axios";
import {
  API_ACCOUNT_DELETE_REQUEST,
  API_ACCOUNT_DELETE_REQUEST_REVOKE,
} from "../Util/URLConstants";
import { displayFormattedDateTime } from "../Util/UtilHelper";
import {
  SESSION_ACCOUNT_DELETION_DATA_KEY,
  SESSION_ACCOUNT_DELETION_PENDING_KEY,
} from "../Util/VMConstants";

const jsonHeaders = { "Content-Type": "application/json" };

export const ACCOUNT_DELETION_STATUS = {
  ACTIVE: "Active",
  INACTIVE: "InActive",
  DELETE_REQUESTED: "DeleteRequested",
  DELETE_APPROVED: "DeleteApproved",
  DELETED: "Deleted",
};

export const ACCOUNT_DELETION_POLL_INTERVAL_MS = 30_000;

export const isPendingDeletionStatus = (status) =>
  status === ACCOUNT_DELETION_STATUS.DELETE_REQUESTED ||
  status === ACCOUNT_DELETION_STATUS.DELETE_APPROVED;

export const getDeletionPayload = (respData) => {
  if (!respData || typeof respData !== "object") return null;
  return respData.account_deletion || respData.deletion_data || respData.data || null;
};

export const getDeletionStatusFromResponse = (respData) => {
  const payload = getDeletionPayload(respData);
  return (
    payload?.status ||
    respData?.account_deletion_status ||
    respData?.deletion_status ||
    null
  );
};

export const resolveLoginDeletionOutcome = (respData) => {
  if (!respData || typeof respData !== "object") {
    return { type: "none" };
  }

  const status = getDeletionStatusFromResponse(respData);
  const payload = getDeletionPayload(respData) || {};

  if (
    respData.status_code === 200 &&
    (respData.account_deletion_pending === true ||
      status === ACCOUNT_DELETION_STATUS.DELETE_REQUESTED)
  ) {
    return {
      type: "pending_login",
      data: {
        ...payload,
        status: status || ACCOUNT_DELETION_STATUS.DELETE_REQUESTED,
      },
    };
  }

  if (status === ACCOUNT_DELETION_STATUS.DELETE_APPROVED) {
    return {
      type: "blocked_approved",
      message:
        respData.message ||
        payload.message ||
        "Your account deletion request has been approved.",
      scheduled_deletion_at:
        payload.scheduled_deletion_at || respData.scheduled_deletion_at,
    };
  }

  if (status === ACCOUNT_DELETION_STATUS.DELETED) {
    return {
      type: "blocked_deleted",
      message:
        respData.message || payload.message || "Your account has been deleted.",
    };
  }

  if (status === ACCOUNT_DELETION_STATUS.INACTIVE) {
    return {
      type: "blocked_inactive",
      message: respData.message || "Your account is inactive.",
    };
  }

  return { type: "none" };
};

export const getLoginDeletionBlockDialog = (outcome, respData) => {
  if (outcome.type === "blocked_approved") {
    const currencySymbol = respData?.user?.country_data?.currency_symbol || "₹";
    return {
      title: "Account scheduled for deletion",
      message:
        outcome.message ||
        formatScheduledDeletionMessage(outcome.scheduled_deletion_at, currencySymbol),
    };
  }

  if (outcome.type === "blocked_deleted") {
    return {
      title: "Account deleted",
      message: outcome.message,
    };
  }

  if (outcome.type === "blocked_inactive") {
    return {
      title: "Account inactive",
      message: outcome.message,
    };
  }

  return null;
};

export const formatScheduledDeletionMessage = (
  scheduledDeletionAt,
  currencySymbol = "₹"
) => {
  if (!scheduledDeletionAt) {
    return "Your account is scheduled for deletion.";
  }

  return `Account scheduled for deletion on ${displayFormattedDateTime(
    scheduledDeletionAt,
    currencySymbol,
    { wallClock: true }
  )}.`;
};

export const setAccountDeletionSession = (sessionFactory, { pending, data }) => {
  if (pending) {
    sessionFactory.add(SESSION_ACCOUNT_DELETION_PENDING_KEY, true);
    if (data) {
      sessionFactory.add(SESSION_ACCOUNT_DELETION_DATA_KEY, data);
    }
    return;
  }

  sessionFactory.remove(SESSION_ACCOUNT_DELETION_PENDING_KEY);
  sessionFactory.remove(SESSION_ACCOUNT_DELETION_DATA_KEY);
};

export const clearAccountDeletionSession = (sessionFactory) => {
  setAccountDeletionSession(sessionFactory, { pending: false });
};

export const hasAccountDeletionPending = (sessionFactory) =>
  sessionFactory.get(SESSION_ACCOUNT_DELETION_PENDING_KEY) === true;

export async function submitAccountDeleteRequest({ user_id, password, reason }) {
  const response = await axios.post(
    API_ACCOUNT_DELETE_REQUEST,
    {
      user_id,
      password,
      reason: reason?.trim() || undefined,
    },
    { headers: jsonHeaders }
  );
  return response.data;
}

export async function getAccountDeleteRequestStatus(user_id) {
  const response = await axios.get(API_ACCOUNT_DELETE_REQUEST, {
    params: { user_id },
    headers: jsonHeaders,
  });
  return response.data;
}

export async function revokeAccountDeleteRequest(user_id) {
  const response = await axios.post(
    API_ACCOUNT_DELETE_REQUEST_REVOKE,
    { user_id },
    { headers: jsonHeaders }
  );
  return response.data;
}
